import { useEffect, useState } from "react";
import type { HeroPerfReport } from "@/lib/hero-perf";
import { evaluateHeroBudget, type BudgetEvaluation } from "@/lib/hero-perf-budgets";

/**
 * Annotates the rendered hero with the timings captured at the moment the
 * image finished decoding: network, load dispatch, decode, total paintable,
 * and LCP (when the browser reports one — LCP entries do not fire inside
 * iframes, so framed runs show "n/a").
 *
 * Values come straight from the `hero-perf` CustomEvent emitted by
 * `instrumentHeroImage()`, and each is coloured against the active budget for
 * the measured breakpoint.
 */
export const HeroTimingOverlay = () => {
  const [report, setReport] = useState<HeroPerfReport | null>(null);
  const [budget, setBudget] = useState<BudgetEvaluation | null>(null);

  useEffect(() => {
    const onPerf = (event: Event) => {
      const detail = (event as CustomEvent<HeroPerfReport>).detail;
      if (!detail) return;
      setReport(detail);
      setBudget(evaluateHeroBudget(detail));
    };
    window.addEventListener("hero-perf", onPerf as EventListener);
    // A report may already have landed before this effect ran (cached image).
    if (window.__heroPerf) {
      setReport(window.__heroPerf);
      setBudget(evaluateHeroBudget(window.__heroPerf));
    }
    return () =>
      window.removeEventListener("hero-perf", onPerf as EventListener);
  }, []);

  if (!report) {
    return (
      <div className="pointer-events-none absolute bottom-2 left-2 z-20 rounded bg-black/70 px-2 py-1 font-mono text-[10px] text-white/70">
        measuring hero…
      </div>
    );
  }

  const ms = (n?: number) => (n != null ? `${n.toFixed(0)} ms` : "n/a");
  const violated = (metric: "lcpMs" | "decodeMs" | "totalMs") =>
    budget?.violations.some((v) => v.metric === metric) ?? false;

  const rows: {
    key: string;
    label: string;
    value: string;
    over?: boolean;
  }[] = [
    { key: "network", label: "network", value: ms(report.deltas.networkMs) },
    { key: "load", label: "load", value: ms(report.deltas.loadMs) },
    {
      key: "decode",
      label: "decoded",
      value: ms(report.deltas.decodeMs),
      over: violated("decodeMs"),
    },
    {
      key: "total",
      label: "paintable",
      value: ms(report.deltas.totalMs),
      over: violated("totalMs"),
    },
    {
      key: "lcp",
      label: "LCP",
      value: ms(report.deltas.lcpMs),
      over: violated("lcpMs"),
    },
  ];

  const transferKb =
    report.transferSizeBytes != null && report.transferSizeBytes > 0
      ? `${(report.transferSizeBytes / 1024).toFixed(1)} KB`
      : "cached";
  const file = (report.currentSrc || "").split("/").pop() ?? "";

  return (
    <div
      aria-hidden
      className="pointer-events-none absolute bottom-2 left-2 z-20 rounded border border-white/20 bg-black/75 px-2.5 py-2 font-mono text-[10px] leading-tight text-white/90 backdrop-blur-sm"
    >
      <div className="mb-1 flex items-center gap-2 text-[9px] uppercase tracking-wider text-white/60">
        <span>
          {budget?.breakpoint ?? "—"} · {report.viewportWidth}px · dpr
          {report.devicePixelRatio}
        </span>
        {budget && budget.violations.length > 0 && (
          <span className="rounded bg-rose-500/80 px-1 text-white">
            over budget
          </span>
        )}
      </div>
      <table className="tabular-nums">
        <tbody>
          {rows.map((r) => (
            <tr key={r.key}>
              <td className="pr-3 text-white/60">{r.label}</td>
              <td className={r.over ? "text-rose-300" : "text-emerald-300"}>
                {r.value}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mt-1 border-t border-white/15 pt-1 text-[9px] text-white/60">
        {transferKb} · {report.naturalWidth ?? "?"}×{report.naturalHeight ?? "?"}
        {file ? ` · ${file.slice(-28)}` : ""}
      </div>
      {report.deltas.lcpMs == null && (
        <div className="text-[9px] text-white/40">
          LCP n/a in iframes — open the page directly
        </div>
      )}
    </div>
  );
};

export default HeroTimingOverlay;
